'use client'

import { PropsWithChildren } from 'react'
import { SideBarNav } from '@/app/core/components/navs/SideBarNav'
import { SideBarNavItem } from '@/app/core/components/navs/SideBarNavItem'
import { TeamSelect } from '@/app/core/components/TeamSelect'
import {
  faBoxArchive,
  faHouseChimney,
  faShare,
  faUser,
} from '@document-crunch/pro-regular-svg-icons'
import { usePathname } from 'next/navigation'
import { useAuth, useFSTrackEvent } from '@/dc-shared-web/src'
import { usePathWithParams } from '@/app/core/hooks/usePathWithParams'
import { FS } from '@/dc-shared-web/src/lib/trackingConstants'
import { useIsProjectEditor } from '@/dc-shared-web/src/hooks/api/user/useIsProjectEditor'
import { useLDFlags } from '@/app/core/hooks/useLDFlags'

const DashboardSideNavItem = () => {
  const { trackEvent } = useFSTrackEvent()

  const pathname = usePathname()
  const dashPathWithParams = usePathWithParams('/dash', [
    'sort',
    'sortDirection',
  ])
  const onDashboardClick = () => trackEvent('Sidebar Dashboard clicked')
  return (
    <SideBarNavItem
      data-testid='sidebar-dashboard'
      href={dashPathWithParams}
      icon={faHouseChimney}
      isActive={
        pathname === '/dash' ||
        pathname.startsWith('/projects') ||
        pathname.startsWith('/folders')
      }
      onClick={onDashboardClick}
    >
      Dashboard
    </SideBarNavItem>
  )
}

const SharedSideNavItem = () => {
  const { trackEvent } = useFSTrackEvent()
  const pathname = usePathname()
  const sharedPathWithParams = usePathWithParams('/shared', [
    'sort',
    'sortDirection',
  ])
  const onSharedClick = () => trackEvent('Sidebar Shared clicked')
  return (
    <SideBarNavItem
      data-testid='sidebar-shared'
      icon={faShare}
      href={sharedPathWithParams}
      isActive={pathname === '/shared'}
      onClick={onSharedClick}
    >
      Shared with me
    </SideBarNavItem>
  )
}

const AssignmentsSideNavItem = () => {
  const { trackEvent } = useFSTrackEvent()
  const pathname = usePathname()
  const sharedPathWithParams = usePathWithParams('/assignments', [
    'sort',
    'sortDirection',
  ])
  const onAssignmentsClick = () => trackEvent('Sidebar Assigned to me clicked')
  return (
    <SideBarNavItem
      data-testid='sidebar-assignments'
      icon={faUser}
      href={sharedPathWithParams}
      isActive={pathname === '/assignments'}
      onClick={onAssignmentsClick}
    >
      Assigned to me
    </SideBarNavItem>
  )
}

const DashboardSideNavLayout = ({ children }: PropsWithChildren) => {
  return (
    <div className='tw-flex tw-relative tw-bg-neutral-50 tw-w-full'>
      {children}
    </div>
  )
}

export default function Layout({ children }: PropsWithChildren) {
  const { trackEvent } = useFSTrackEvent()
  const pathname = usePathname()
  const { user } = useAuth()
  const {
    flags: { rr1219Assignment },
  } = useLDFlags()

  const isUnaffiliatedUser = user._isUnaffiliatedUser
  const isProjectEditor = useIsProjectEditor()

  const archivedPathWithParams = usePathWithParams('/archived', [
    'sort',
    'sortDirection',
  ])

  const track = (name: string) => {
    trackEvent(FS.SIDEBAR_ITEM_CLICKED, {
      name,
    })
  }

  if (isUnaffiliatedUser) {
    return (
      <DashboardSideNavLayout>
        <SideBarNav>
          <SharedSideNavItem />
        </SideBarNav>
        <div className='tw-p-6 tw-flex-grow tw-overflow-y-auto tw-h-screen-minus-header'>
          {children}
        </div>
      </DashboardSideNavLayout>
    )
  }

  if (isProjectEditor) {
    return (
      <DashboardSideNavLayout>
        <SideBarNav data-testid='dashboard-sidebar'>
          <div className='tw-pb-2'>
            <TeamSelect showAllTeamsOption={user.is_super_admin} />
          </div>
          <DashboardSideNavItem />
          <hr className='tw-border-neutral-100' />
          <SharedSideNavItem />
          {rr1219Assignment && <AssignmentsSideNavItem />}
        </SideBarNav>
        <div className='tw-p-6 tw-flex-grow tw-overflow-y-auto'>{children}</div>
      </DashboardSideNavLayout>
    )
  }

  return (
    <DashboardSideNavLayout>
      <SideBarNav data-testid='dashboard-sidebar'>
        <div className='tw-pb-2'>
          <TeamSelect showAllTeamsOption={user.is_super_admin} />
        </div>
        <DashboardSideNavItem />
        <SideBarNavItem
          data-testid='sidebar-archived'
          href={archivedPathWithParams}
          icon={faBoxArchive}
          isActive={pathname === '/archived'}
          onClick={() => track('Sidebar Archived clicked')}
        >
          Archived
        </SideBarNavItem>
        <hr className='tw-border-neutral-100' />
        <SharedSideNavItem />
        {rr1219Assignment && <AssignmentsSideNavItem />}
      </SideBarNav>
      <div className='tw-p-6 tw-flex-grow tw-overflow-y-auto'>{children}</div>
    </DashboardSideNavLayout>
  )
}
